export type UiLocale = 'pt-BR' | 'es' | 'en-US';
export type CountryCode =
  | 'BR'
  | 'AR'
  | 'UY'
  | 'PY'
  | 'CO'
  | 'MX'
  | 'US'
  | 'CA'
  | 'AU'
  | 'NZ'
  | 'ES';
export type CurrencyCode =
  | 'BRL'
  | 'ARS'
  | 'UYU'
  | 'PYG'
  | 'COP'
  | 'MXN'
  | 'USD'
  | 'CAD'
  | 'AUD'
  | 'NZD'
  | 'EUR';
export type WeightUnit = 'kg' | 'lb';
export type DateFormatPref = 'dmy' | 'mdy' | 'ymd';
export type TimeFormatPref = 'h24' | 'h12';

export type LocalePrefs = {
  uiLocale: UiLocale;
  country: CountryCode;
  currency: CurrencyCode;
  weightUnit: WeightUnit;
  dateFormat: DateFormatPref;
  timeFormat: TimeFormatPref;
  timezone: string;
};

export type CountryOption = {
  value: CountryCode;
  label: string;
  currency: CurrencyCode;
  weightUnit: WeightUnit;
  dateFormat: DateFormatPref;
  timeFormat: TimeFormatPref;
  timezone: string;
};

export const UI_LOCALE_OPTIONS: { value: UiLocale; label: string }[] = [
  { value: 'pt-BR', label: 'Português (Brasil)' },
  { value: 'es', label: 'Español' },
  { value: 'en-US', label: 'English (US)' },
];

export const COUNTRY_OPTIONS: CountryOption[] = [
  {
    value: 'BR',
    label: 'Brasil',
    currency: 'BRL',
    weightUnit: 'kg',
    dateFormat: 'dmy',
    timeFormat: 'h24',
    timezone: 'America/Sao_Paulo',
  },
  {
    value: 'AR',
    label: 'Argentina',
    currency: 'ARS',
    weightUnit: 'kg',
    dateFormat: 'dmy',
    timeFormat: 'h24',
    timezone: 'America/Argentina/Buenos_Aires',
  },
  {
    value: 'UY',
    label: 'Uruguai',
    currency: 'UYU',
    weightUnit: 'kg',
    dateFormat: 'dmy',
    timeFormat: 'h24',
    timezone: 'America/Montevideo',
  },
  {
    value: 'PY',
    label: 'Paraguai',
    currency: 'PYG',
    weightUnit: 'kg',
    dateFormat: 'dmy',
    timeFormat: 'h24',
    timezone: 'America/Asuncion',
  },
  {
    value: 'CO',
    label: 'Colômbia',
    currency: 'COP',
    weightUnit: 'kg',
    dateFormat: 'dmy',
    timeFormat: 'h24',
    timezone: 'America/Bogota',
  },
  {
    value: 'MX',
    label: 'México',
    currency: 'MXN',
    weightUnit: 'kg',
    dateFormat: 'dmy',
    timeFormat: 'h24',
    timezone: 'America/Mexico_City',
  },
  {
    value: 'US',
    label: 'Estados Unidos',
    currency: 'USD',
    weightUnit: 'lb',
    dateFormat: 'mdy',
    timeFormat: 'h12',
    timezone: 'America/Chicago',
  },
  {
    value: 'CA',
    label: 'Canadá',
    currency: 'CAD',
    weightUnit: 'kg',
    dateFormat: 'ymd',
    timeFormat: 'h24',
    timezone: 'America/Toronto',
  },
  {
    value: 'AU',
    label: 'Austrália',
    currency: 'AUD',
    weightUnit: 'kg',
    dateFormat: 'dmy',
    timeFormat: 'h12',
    timezone: 'Australia/Sydney',
  },
  {
    value: 'NZ',
    label: 'Nova Zelândia',
    currency: 'NZD',
    weightUnit: 'kg',
    dateFormat: 'dmy',
    timeFormat: 'h12',
    timezone: 'Pacific/Auckland',
  },
  {
    value: 'ES',
    label: 'Espanha',
    currency: 'EUR',
    weightUnit: 'kg',
    dateFormat: 'dmy',
    timeFormat: 'h24',
    timezone: 'Europe/Madrid',
  },
];

export const WEIGHT_UNIT_OPTIONS: { value: WeightUnit; label: string }[] = [
  { value: 'kg', label: 'Quilograma (kg)' },
  { value: 'lb', label: 'Libra (lb)' },
];

export const DATE_FORMAT_OPTIONS: { value: DateFormatPref; label: string; example: string }[] = [
  { value: 'dmy', label: 'Dia / Mês / Ano', example: '29/07/2026' },
  { value: 'mdy', label: 'Mês / Dia / Ano', example: '07/29/2026' },
  { value: 'ymd', label: 'Ano - Mês - Dia', example: '2026-07-29' },
];

export const TIME_FORMAT_OPTIONS: { value: TimeFormatPref; label: string; example: string }[] = [
  { value: 'h24', label: '24 horas', example: '18:45' },
  { value: 'h12', label: '12 horas (AM/PM)', example: '6:45 PM' },
];

export const TIMEZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'America/Sao_Paulo', label: 'Brasília (GMT-3)' },
  { value: 'America/Manaus', label: 'Manaus (GMT-4)' },
  { value: 'America/Cuiaba', label: 'Cuiabá (GMT-4)' },
  { value: 'America/Fortaleza', label: 'Fortaleza (GMT-3)' },
  { value: 'America/Belem', label: 'Belém (GMT-3)' },
  { value: 'America/Rio_Branco', label: 'Rio Branco (GMT-5)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (GMT-3)' },
  { value: 'America/Montevideo', label: 'Montevidéu (GMT-3)' },
  { value: 'America/Asuncion', label: 'Assunção (GMT-4/-3)' },
  { value: 'America/Bogota', label: 'Bogotá (GMT-5)' },
  { value: 'America/Mexico_City', label: 'Cidade do México (GMT-6)' },
  { value: 'America/Chicago', label: 'Chicago (GMT-6/-5)' },
  { value: 'America/New_York', label: 'Nova York (GMT-5/-4)' },
  { value: 'America/Denver', label: 'Denver (GMT-7/-6)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8/-7)' },
  { value: 'America/Toronto', label: 'Toronto (GMT-5/-4)' },
  { value: 'America/Edmonton', label: 'Edmonton (GMT-7/-6)' },
  { value: 'America/Vancouver', label: 'Vancouver (GMT-8/-7)' },
  { value: 'Australia/Sydney', label: 'Sydney (GMT+10/+11)' },
  { value: 'Australia/Brisbane', label: 'Brisbane (GMT+10)' },
  { value: 'Australia/Perth', label: 'Perth (GMT+8)' },
  { value: 'Pacific/Auckland', label: 'Auckland (GMT+12/+13)' },
  { value: 'Europe/Madrid', label: 'Madrid (GMT+1/+2)' },
  { value: 'UTC', label: 'UTC' },
];

export const DEFAULT_LOCALE_PREFS: LocalePrefs = {
  uiLocale: 'pt-BR',
  country: 'BR',
  currency: 'BRL',
  weightUnit: 'kg',
  dateFormat: 'dmy',
  timeFormat: 'h24',
  timezone: 'America/Sao_Paulo',
};

const KG_PER_LB = 0.45359237;
const LB_PER_KG = 2.2046226218;

/** Locale BCP-47 usado em Intl / formatação regional de números. */
export function intlLocaleForCountry(country: CountryCode): string {
  const map: Record<CountryCode, string> = {
    BR: 'pt-BR',
    AR: 'es-AR',
    UY: 'es-UY',
    PY: 'es-PY',
    CO: 'es-CO',
    MX: 'es-MX',
    US: 'en-US',
    CA: 'en-CA',
    AU: 'en-AU',
    NZ: 'en-NZ',
    ES: 'es-ES',
  };
  return map[country] ?? 'pt-BR';
}

export function countryOption(country: CountryCode): CountryOption {
  return COUNTRY_OPTIONS.find((c) => c.value === country) ?? COUNTRY_OPTIONS[0];
}

export function defaultsForCountry(country: CountryCode): Pick<
  LocalePrefs,
  'currency' | 'weightUnit' | 'dateFormat' | 'timeFormat' | 'timezone'
> {
  const opt = countryOption(country);
  return {
    currency: opt.currency,
    weightUnit: opt.weightUnit,
    dateFormat: opt.dateFormat,
    timeFormat: opt.timeFormat,
    timezone: opt.timezone,
  };
}

export function defaultCurrencyForCountry(country: CountryCode): CurrencyCode {
  return countryOption(country).currency;
}

export function normalizeUiLocale(value: string | null | undefined): UiLocale {
  if (value === 'pt-BR' || value === 'es' || value === 'en-US') return value;
  return 'pt-BR';
}

export function normalizeCountry(value: string | null | undefined): CountryCode {
  const upper = (value ?? 'BR').toUpperCase();
  if (COUNTRY_OPTIONS.some((c) => c.value === upper)) return upper as CountryCode;
  return 'BR';
}

export function normalizeCurrency(
  value: string | null | undefined,
  country: CountryCode = 'BR',
): CurrencyCode {
  const upper = (value ?? '').toUpperCase();
  const allowed = COUNTRY_OPTIONS.map((c) => c.currency);
  if (allowed.includes(upper as CurrencyCode)) return upper as CurrencyCode;
  return defaultCurrencyForCountry(country);
}

export function normalizeWeightUnit(value: string | null | undefined): WeightUnit {
  return value === 'lb' ? 'lb' : 'kg';
}

export function normalizeDateFormat(value: string | null | undefined): DateFormatPref {
  if (value === 'mdy' || value === 'ymd' || value === 'dmy') return value;
  return 'dmy';
}

export function normalizeTimeFormat(value: string | null | undefined): TimeFormatPref {
  return value === 'h12' ? 'h12' : 'h24';
}

export function normalizeTimezone(value: string | null | undefined): string {
  if (value && TIMEZONE_OPTIONS.some((t) => t.value === value)) return value;
  return DEFAULT_LOCALE_PREFS.timezone;
}

export function kgToDisplay(kg: number, unit: WeightUnit = getLocalePrefs().weightUnit): number {
  return unit === 'lb' ? kg * LB_PER_KG : kg;
}

export function displayToKg(value: number, unit: WeightUnit = getLocalePrefs().weightUnit): number {
  return unit === 'lb' ? value * KG_PER_LB : value;
}

export function weightUnitLabel(unit: WeightUnit = getLocalePrefs().weightUnit): string {
  return unit === 'lb' ? 'lb' : 'kg';
}

let activePrefs: LocalePrefs = { ...DEFAULT_LOCALE_PREFS };

type LocaleListener = () => void;
const localeListeners = new Set<LocaleListener>();

export function subscribeLocalePrefs(listener: LocaleListener): () => void {
  localeListeners.add(listener);
  return () => localeListeners.delete(listener);
}

function notifyLocaleListeners() {
  localeListeners.forEach((listener) => listener());
}

export function bumpLocaleListeners(): void {
  notifyLocaleListeners();
}

export function getLocalePrefs(): LocalePrefs {
  return activePrefs;
}

export function setLocalePrefs(prefs: Partial<LocalePrefs>): LocalePrefs {
  const country = prefs.country ? normalizeCountry(prefs.country) : activePrefs.country;
  const next: LocalePrefs = {
    uiLocale: prefs.uiLocale ? normalizeUiLocale(prefs.uiLocale) : activePrefs.uiLocale,
    country,
    currency: prefs.currency
      ? normalizeCurrency(prefs.currency, country)
      : prefs.country
        ? defaultCurrencyForCountry(country)
        : activePrefs.currency,
    weightUnit:
      prefs.weightUnit != null
        ? normalizeWeightUnit(prefs.weightUnit)
        : activePrefs.weightUnit,
    dateFormat:
      prefs.dateFormat != null
        ? normalizeDateFormat(prefs.dateFormat)
        : activePrefs.dateFormat,
    timeFormat:
      prefs.timeFormat != null
        ? normalizeTimeFormat(prefs.timeFormat)
        : activePrefs.timeFormat,
    timezone:
      prefs.timezone != null ? normalizeTimezone(prefs.timezone) : activePrefs.timezone,
  };

  const changed =
    next.uiLocale !== activePrefs.uiLocale ||
    next.country !== activePrefs.country ||
    next.currency !== activePrefs.currency ||
    next.weightUnit !== activePrefs.weightUnit ||
    next.dateFormat !== activePrefs.dateFormat ||
    next.timeFormat !== activePrefs.timeFormat ||
    next.timezone !== activePrefs.timezone;

  activePrefs = next;

  if (typeof document !== 'undefined') {
    document.documentElement.lang = activePrefs.uiLocale;
  }

  if (changed) notifyLocaleListeners();

  return activePrefs;
}
